import React from "react";
import PropTypes from "prop-types";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import CustomChip from "components/common/Chip";
import { getFormattedTimeAndDate } from "components/Screens/CommonUtils";

import "./ManualPaymentUpdate.scss";

const STAGE_STATUS_LABELS = {
  pending: "Processing",
  processing: "Processing",
  paid: "Paid",
  failed: "Failed",
};

const statusLabel = (status) => STAGE_STATUS_LABELS[String(status || "").toLowerCase()] || status || "";

function ManualPaymentUpdate({ audit, documents, onDownloadProof, paymentMethod, referenceNumber, history }) {
  const handleDownload = () => {
    if (onDownloadProof) {
      onDownloadProof();
    }
  };

  const hasHistory = Array.isArray(history) && history.length > 0;

  const renderStage = (stage, key) => {
    const isWireStage = stage?.paymentMethod === "wire";
    const stageDocs = Array.isArray(stage?.documents) ? stage.documents : [];
    const stageNote = stage?.note || "";
    const stageOn = stage?.recordedAt ? getFormattedTimeAndDate(stage.recordedAt) : "";

    return (
      <div key={key} className="manual-payment-update-stage">
        <div className="manual-payment-update-field manual-payment-update-field--status">
          <p className="manual-payment-update-label">Status</p>
          <p className="manual-payment-update-value">{statusLabel(stage?.status)}</p>
        </div>

        {stageOn && (
          <div className="manual-payment-update-field manual-payment-update-field--updated">
            <p className="manual-payment-update-label">Updated on</p>
            <p className="manual-payment-update-value">{stageOn}</p>
          </div>
        )}

        {isWireStage && stage?.referenceNumber && (
          <div className="manual-payment-update-field manual-payment-update-field--reference">
            <p className="manual-payment-update-label">Payment reference</p>
            <p className="manual-payment-update-value">{stage.referenceNumber}</p>
          </div>
        )}

        {!isWireStage && stageDocs.length > 0 && (
          <div className="manual-payment-update-field manual-payment-update-field--reference">
            <p className="manual-payment-update-label">Proof of payment</p>
            <CustomChip
              label="Download"
              onClick={() => onDownloadProof && onDownloadProof(stageDocs)}
              endIcon={<FileDownloadOutlinedIcon />}
              size="small"
              variant="filled"
              color="default"
            />
          </div>
        )}

        {stageNote && (
          <div className="manual-payment-update-field manual-payment-update-field--note">
            <p className="manual-payment-update-label">Note</p>
            <p className="manual-payment-update-value">{stageNote}</p>
          </div>
        )}
      </div>
    );
  };

  // Fallback single-summary view (legacy payments recorded before per-stage history existed)
  const isWireTransfer = paymentMethod === "wire";
  const updatedBy = `${audit?.userInfo?.firstName || ""} ${audit?.userInfo?.lastName || ""}`.trim() || "";
  const updatedOn = audit?.updatedAt ? getFormattedTimeAndDate(audit.updatedAt) : "";
  const note = audit?.note || "";
  const hasDocuments = Array.isArray(documents) && documents.length > 0;

  return (
    <div className="manual-payment-update-wrapper">
      <div className="manual-payment-update-header">
        <p className="manual-payment-update-header-text">The status for this payment was updated manually</p>
      </div>

      {hasHistory ? (
        <div className="manual-payment-update-stages">{history.map((stage, index) => renderStage(stage, index))}</div>
      ) : (
        <div className="manual-payment-update-content">
          {updatedBy && (
            <div className="manual-payment-update-field">
              <p className="manual-payment-update-label">Updated by</p>
              <p className="manual-payment-update-value">{updatedBy}</p>
            </div>
          )}

          {updatedOn && (
            <div className="manual-payment-update-field">
              <p className="manual-payment-update-label">Updated on</p>
              <p className="manual-payment-update-value">{updatedOn}</p>
            </div>
          )}

          {isWireTransfer && referenceNumber && (
            <div className="manual-payment-update-field">
              <p className="manual-payment-update-label">Payment reference</p>
              <p className="manual-payment-update-value">{referenceNumber}</p>
            </div>
          )}

          {!isWireTransfer && hasDocuments && (
            <div className="manual-payment-update-field">
              <p className="manual-payment-update-label">Proof of payment</p>
              <CustomChip
                label="Download"
                onClick={handleDownload}
                endIcon={<FileDownloadOutlinedIcon />}
                size="small"
                variant="filled"
                color="default"
              />
            </div>
          )}

          {note && (
            <div className="manual-payment-update-field">
              <p className="manual-payment-update-label">Note</p>
              <p className="manual-payment-update-value">{note}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

ManualPaymentUpdate.propTypes = {
  audit: PropTypes.shape({
    user: PropTypes.string,
    updatedAt: PropTypes.string,
    note: PropTypes.string,
  }),
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      displayName: PropTypes.string,
      s3Key: PropTypes.string,
      size: PropTypes.number,
      path: PropTypes.string,
      contentType: PropTypes.string,
    })
  ),
  history: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.string,
      paymentMethod: PropTypes.string,
      referenceNumber: PropTypes.string,
      note: PropTypes.string,
      paymentDate: PropTypes.string,
      recordedAt: PropTypes.string,
      documents: PropTypes.array,
    })
  ),
  onDownloadProof: PropTypes.func,
  paymentMethod: PropTypes.string,
  referenceNumber: PropTypes.string,
};

ManualPaymentUpdate.defaultProps = {
  audit: {
    user: "",
    updatedAt: "",
    note: "",
  },
  documents: [],
  history: [],
  onDownloadProof: null,
  paymentMethod: "",
  referenceNumber: "",
};

export default ManualPaymentUpdate;
